/**
 * Thin promise wrappers over the browser Geolocation and Permissions APIs.
 */

export type GeolocationPermission = 'granted' | 'prompt' | 'denied' | 'unsupported';

export type DevicePositionFailure = 'denied' | 'unavailable' | 'timeout';

export interface Coordinates {
  lat: number;
  lng: number;
}

export interface DevicePosition extends Coordinates {
  /** Radius in meters the browser is 95% sure the device is within. */
  accuracy: number;
}

// A fix this tight places the device at a street address. iOS "Precise
// Location: Off" and Android "Approximate" report kilometers.
export const PRECISE_FIX_METERS = 100;

// A precise fix fills in the street address. A phone can take a while to get
// one, and some browsers refuse high accuracy outright, so either falls back to
// a coarse fix for the county.
const PRECISE_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 8_000,
  maximumAge: 5 * 60 * 1000,
};

const COARSE_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  timeout: 10_000,
  maximumAge: 60 * 60 * 1000,
};

export const isGeolocationSupported = (): boolean =>
  typeof navigator !== 'undefined' && 'geolocation' in navigator;

/**
 * The page's geolocation permission without prompting. Browsers that lack the
 * Permissions API (or reject the ``geolocation`` name) report ``prompt``: the
 * only way to learn more there is to ask, which needs a user gesture.
 */
export const queryGeolocationPermission = async (): Promise<GeolocationPermission> => {
  if (!isGeolocationSupported()) return 'unsupported';
  try {
    const status = await navigator.permissions?.query({ name: 'geolocation' });
    return status?.state ?? 'prompt';
  } catch {
    return 'prompt';
  }
};

/**
 * Calls ``onChange`` when the voter changes the page's geolocation permission,
 * such as unblocking it in site settings. Returns the unsubscribe.
 */
export const watchGeolocationPermission = (
  onChange: (state: GeolocationPermission) => void
): (() => void) => {
  let status: PermissionStatus | undefined;
  let stopped = false;
  const handle = () => {
    if (status) onChange(status.state);
  };
  if (isGeolocationSupported()) {
    navigator.permissions
      ?.query({ name: 'geolocation' })
      .then((result) => {
        if (stopped) return;
        status = result;
        status.addEventListener('change', handle);
      })
      .catch(() => {});
  }
  return () => {
    stopped = true;
    status?.removeEventListener('change', handle);
  };
};

const requestPosition = (options: PositionOptions): Promise<DevicePosition> =>
  new Promise((resolve, reject) => {
    if (!isGeolocationSupported()) {
      reject('unavailable' satisfies DevicePositionFailure);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) =>
        resolve({ lat: coords.latitude, lng: coords.longitude, accuracy: coords.accuracy }),
      (error) => {
        const reason: DevicePositionFailure =
          error.code === error.PERMISSION_DENIED
            ? 'denied'
            : error.code === error.TIMEOUT
              ? 'timeout'
              : 'unavailable';
        reject(reason);
      },
      options
    );
  });

/**
 * Current position, rejecting with a ``DevicePositionFailure``. ``precise``
 * only after a tap: high accuracy can wake a phone's GPS.
 */
export const getDevicePosition = async ({ precise = true } = {}): Promise<DevicePosition> => {
  if (!precise) return requestPosition(COARSE_OPTIONS);
  try {
    return await requestPosition(PRECISE_OPTIONS);
  } catch (reason) {
    if (reason === 'denied') throw reason;
    return requestPosition(COARSE_OPTIONS);
  }
};
